"use client";

import { useState } from "react";
import { useAuthContext } from "@/contexts/common/AuthProvider";
import { useSessionTimeoutContext } from "@/contexts/common/SessionTimeoutProvider";

export default function SessionDebugInfo() {
  const [isOpen, setIsOpen] = useState(false);
  const [showClaims, setShowClaims] = useState(false);
  const { user } = useAuthContext();
  const {
    isLoaded,
    idleTimeoutMinutes,
    sessionExpiresAt,
    idleSecondsRemaining,
    absoluteSecondsRemaining,
    claims: accessTokenClaims,
  } = useSessionTimeoutContext();

  // Only show in development
  if (process.env.NODE_ENV !== "development") {
    return null;
  }

  if (!isLoaded) {
    return null;
  }

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 right-4 z-50 bg-gray-800 text-white p-2 rounded-full shadow-lg opacity-50 hover:opacity-100 text-xs flex items-center justify-center w-8 h-8"
        title="Session Debug Info"
      >
        ⏱️
      </button>
    );
  }

  const formatTime = (seconds: number | null) => {
    if (seconds === null) return "N/A";
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  return (
    <div className="fixed bottom-4 right-4 z-50 bg-gray-900 text-white p-4 rounded-lg shadow-xl border border-gray-700 w-64 text-xs font-mono opacity-90 hover:opacity-100 transition-opacity">
      <div className="flex justify-between items-center mb-2 pb-2 border-b border-gray-700">
        <h3 className="font-bold text-gray-200">Session Debug</h3>
        <button onClick={() => setIsOpen(false)} className="text-gray-400 hover:text-white">
          ✕
        </button>
      </div>

      <div className="space-y-1">
        <div className="flex justify-between">
          <span className="text-gray-400">Idle Config:</span>
          <span>{idleTimeoutMinutes}m</span>
        </div>

        <div className="flex justify-between">
          <span className="text-gray-400">Idle Remaining:</span>
          <span
            className={idleSecondsRemaining < 120 ? "text-red-400 font-bold" : "text-green-400"}
          >
            {formatTime(idleSecondsRemaining)}
          </span>
        </div>

        <div className="flex justify-between">
          <span className="text-gray-400">Abs Remaining:</span>
          <span
            className={
              absoluteSecondsRemaining && absoluteSecondsRemaining < 300
                ? "text-red-400"
                : "text-blue-400"
            }
          >
            {formatTime(absoluteSecondsRemaining)}
          </span>
        </div>

        <div className="flex justify-between text-[10px] text-gray-500 mt-2 border-t border-gray-800 pt-1">
          <span>Expires At:</span>
          <span>
            {sessionExpiresAt ? new Date(sessionExpiresAt * 1000).toLocaleTimeString() : "Never"}
          </span>
        </div>

        <div className="mt-2 border-t border-gray-700 pt-2">
          <button
            onClick={() => setShowClaims(!showClaims)}
            className="w-full text-left text-gray-400 hover:text-white flex justify-between items-center"
          >
            <span>Token Claims</span>
            <span>{showClaims ? "▼" : "▶"}</span>
          </button>

          {showClaims && (
            <div className="mt-2 p-2 bg-gray-950 rounded border border-gray-800 overflow-auto max-h-48 text-[10px] space-y-2">
              {user && (
                <div>
                  <div className="font-bold text-gray-400 mb-1">ID Token (UI)</div>
                  <pre>{JSON.stringify(user, null, 2)}</pre>
                </div>
              )}
              {accessTokenClaims && (
                <div className="border-t border-gray-800 pt-2 mt-2">
                  <div className="font-bold text-gray-400 mb-1">Access Token (API)</div>
                  <pre>{JSON.stringify(accessTokenClaims, null, 2)}</pre>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
